import type { PumpStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import { PumpBasalRateState } from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"

import { usePolyglot } from "src/contexts"
import { DataRecency } from "src/types"
import {
  serverInsulinToUserInsulin,
  round,
} from "src/utils"

export interface Props extends ComponentPropsWithoutRef<"div"> {
  pumpStatus: PumpStatus;
  pumpStatusRecency: DataRecency;
  // an optional class name to style the value differently from the unit
  valueClassName?: string;
}

export const PumpBasalRate = forwardRef<HTMLDivElement, Props>(
  function PumpBasalRate(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      pumpStatus,
      pumpStatusRecency,
      valueClassName,
      ...divProps
    } = props

    const {
      basal_rate: basalRate,
      basal_rate_state: basalRateState,
    } = pumpStatus

    const polyglot = usePolyglot()

    const basalRateString: string = useMemo(
      () => {
        if (basalRate === null || pumpStatusRecency === DataRecency.Stale) {
          return "---"
        }

        if (basalRateState === PumpBasalRateState.NoData) {
          return "---"
        }

        if (basalRateState === PumpBasalRateState.AllDeliveriesStopped) {
          return ""
        }

        return polyglot.t(
          "unit.num_units_per_hour",
          {
            value: (
              <span className={valueClassName}>
                {round(
                  serverInsulinToUserInsulin(basalRate),
                  3,
                ).toLocaleString(polyglot.locale())
                }
              </span>
            ),
          },
        )
      },
      [
        pumpStatusRecency,
        basalRate,
        basalRateState,
        valueClassName,
        polyglot,
      ],
    )

    return (
      <div
        {...divProps}
        ref={propRef}
      >
        {basalRateString}
      </div>
    )
  },
)
